import { useState, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'sonner'
import { useWorkflow } from '@/contexts/WorkflowContext'
import { useCadence } from '@/contexts/CadenceContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ArrowLeft, Bot, CheckCircle, XCircle, Clock, AlertTriangle, Loader2, Ban, RotateCcw, UserPlus } from 'lucide-react'
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog'
import { WORKFLOW_RUN_STATUS_CONFIG } from '@/types/workflow'

const STATUS_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  running: Loader2,
  waiting: Clock,
  completed: CheckCircle,
  failed: XCircle,
  paused: AlertTriangle,
}

// Runs that are still "in flight" — cancel is available for these.
const ACTIVE_RUN_STATUSES = new Set(['running', 'waiting', 'paused'])
// Runs that can be re-armed from their current node.
const RETRYABLE_RUN_STATUSES = new Set(['failed', 'paused'])

export function WorkflowRuns() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isAgentMode = location.pathname.startsWith('/agents/workflows')
  const basePath = isAgentMode ? '/agents/workflows' : '/workflows'
  const {
    workflows,
    getWorkflowRuns,
    getWorkflowEventLog,
    eventLogTruncated,
    cancelRun,
    retryRun,
    cancelAllActiveRuns,
    isRunActionPending,
    enrollLeads,
  } = useWorkflow()
  const { leads } = useCadence()

  const [confirmCancelAll, setConfirmCancelAll] = useState(false)
  const [enrollOpen, setEnrollOpen] = useState(false)
  const [enrollSearch, setEnrollSearch] = useState('')
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set())
  const [enrolling, setEnrolling] = useState(false)

  const workflow = workflows.find((w) => w.id === id)
  const runs = useMemo(() => (id ? getWorkflowRuns(id) : []), [id, getWorkflowRuns])
  const activeRunCount = runs.filter((r) => ACTIVE_RUN_STATUSES.has(r.status)).length

  // CA-015: enrollLeads had no caller — lead workflows were a dead-end. Only
  // offer enrollment for lead workflows that are ACTIVE (the executor processes
  // any running run, so an inactive workflow would just create zombies).
  const enrolledLeadIds = useMemo(
    () => new Set(runs.map((r) => r.lead_id).filter(Boolean) as string[]),
    [runs]
  )
  const assignableLeads = useMemo(() => {
    const q = enrollSearch.trim().toLowerCase()
    return leads
      .filter((l) => !enrolledLeadIds.has(l.id))
      .filter((l) => {
        if (!q) return true
        const hay = `${l.first_name} ${l.last_name} ${l.company || ''} ${l.title || ''}`.toLowerCase()
        return hay.includes(q)
      })
      .slice(0, 200)
  }, [leads, enrolledLeadIds, enrollSearch])

  const toggleLead = (leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev)
      if (next.has(leadId)) next.delete(leadId)
      else next.add(leadId)
      return next
    })
  }

  const handleEnroll = async () => {
    if (!id || selectedLeadIds.size === 0) return
    setEnrolling(true)
    try {
      const { enrolled, skipped } = await enrollLeads(id, Array.from(selectedLeadIds))
      toast.success(
        `${enrolled} lead${enrolled !== 1 ? 's' : ''} asignado${enrolled !== 1 ? 's' : ''}` +
          (skipped > 0 ? ` · ${skipped} ya estaba${skipped !== 1 ? 'n' : ''} enrolado${skipped !== 1 ? 's' : ''}` : '')
      )
      setSelectedLeadIds(new Set())
      setEnrollSearch('')
      setEnrollOpen(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudieron asignar los leads')
    } finally {
      setEnrolling(false)
    }
  }

  const handleCancelRun = async (runId: string) => {
    try {
      await cancelRun(runId)
      toast.success('Run cancelado')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo cancelar el run')
    }
  }

  const handleRetryRun = async (runId: string) => {
    try {
      await retryRun(runId)
      toast.success('Run reintentado')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo reintentar el run')
    }
  }

  const handleCancelAll = async () => {
    if (!id) return
    try {
      const count = await cancelAllActiveRuns(id)
      toast.success(count > 0 ? `${count} run${count !== 1 ? 's' : ''} cancelado${count !== 1 ? 's' : ''}` : 'No había runs activos')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudieron cancelar los runs')
    }
  }

  if (!workflow) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Workflow no encontrado</p>
      </div>
    )
  }

  return (
    <div className={isAgentMode ? 'min-h-screen bg-background' : ''}>
      {isAgentMode && (
        <div className="border-b bg-background/95 backdrop-blur px-6 py-3 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/${id}`)}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Builder
          </Button>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Bot className="h-4 w-4" />
            <span>Agent Workflows</span>
            <span>/</span>
            <span className="text-foreground font-medium">{workflow.name}</span>
            <span>/</span>
            <span className="text-foreground font-medium">Runs</span>
          </div>
        </div>
      )}
      <div className="p-8">
        {!isAgentMode && (
          <Button variant="ghost" className="mb-4" onClick={() => navigate(`${basePath}/${id}`)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Builder
          </Button>
        )}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-[28px] font-bold tracking-tight font-heading">{workflow.name} — Runs</h1>
            <p className="text-muted-foreground">
              {runs.length} run{runs.length !== 1 ? 's' : ''} {isAgentMode ? 'ejecutados' : 'enrolled'}
              {activeRunCount > 0 && ` · ${activeRunCount} activo${activeRunCount !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isAgentMode && workflow.status === 'active' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEnrollOpen(true)}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Asignar leads
              </Button>
            )}
            {activeRunCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setConfirmCancelAll(true)}
                disabled={isRunActionPending}
              >
                <Ban className="mr-2 h-4 w-4" />
                Cancelar todos los activos
              </Button>
            )}
          </div>
        </div>

        {/* For lead workflows that are not active, explain why enrollment is off. */}
        {!isAgentMode && workflow.status !== 'active' && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-muted bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>Activa el workflow para poder asignarle leads.</span>
          </div>
        )}

        {eventLogTruncated && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-xs text-amber-600">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Mostrando los eventos más recientes de la organización (límite 500).
              Los pasos de runs antiguos pueden aparecer incompletos.
            </span>
          </div>
        )}

        {runs.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground">
                {isAgentMode ? 'No hay ejecuciones aún. Activa el workflow para empezar.' : 'Sin leads asignados a este workflow aún'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {runs.map((run) => {
              const lead = !isAgentMode ? leads.find((l) => l.id === run.lead_id) : null
              const statusConfig = WORKFLOW_RUN_STATUS_CONFIG[run.status]
              const StatusIcon = STATUS_ICONS[run.status] || Clock
              const currentNode = workflow.graph_json.nodes.find(
                (n) => n.id === run.current_node_id
              )
              const eventLog = getWorkflowEventLog(run.id)
              const context = run.context_json as Record<string, unknown> || {}
              const startTime = new Date(run.started_at)
              const duration = run.status === 'completed'
                ? Math.round((new Date(run.updated_at).getTime() - startTime.getTime()) / 1000)
                : null

              return (
                <Card key={run.id}>
                  <CardHeader className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <StatusIcon className={`h-5 w-5 ${
                          run.status === 'completed' ? 'text-emerald-500' :
                          run.status === 'failed' ? 'text-red-500' :
                          run.status === 'running' ? 'text-blue-500 animate-spin' :
                          'text-amber-500'
                        }`} />
                        <div>
                          <CardTitle className="text-base">
                            {isAgentMode
                              ? `Run ${run.id.substring(0, 8)}`
                              : lead ? `${lead.first_name} ${lead.last_name}` : 'Unknown Lead'
                            }
                          </CardTitle>
                          <p className="text-sm text-muted-foreground">
                            {run.status === 'completed'
                              ? `Completado${duration ? ` en ${duration}s` : ''}`
                              : `En: ${(currentNode?.data?.label as string) || run.current_node_id || '—'}`
                            }
                            {run.waiting_for_event && ` · Esperando ${run.waiting_for_event}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={run.status === 'completed' ? 'default' : 'secondary'}>
                          {statusConfig.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {startTime.toLocaleString()}
                        </span>
                        {/* CA-013: per-run controls. Cancel a stuck/zombie run;
                            retry a failed/paused one from its current node. */}
                        {ACTIVE_RUN_STATUSES.has(run.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-destructive hover:text-destructive"
                            onClick={() => handleCancelRun(run.id)}
                            disabled={isRunActionPending}
                            title="Cancelar run"
                          >
                            <Ban className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {RETRYABLE_RUN_STATUSES.has(run.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={() => handleRetryRun(run.id)}
                            disabled={isRunActionPending}
                            title="Reintentar run"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  {/* Event log timeline for agent workflows */}
                  {isAgentMode && Array.isArray(eventLog) && eventLog.length > 0 ? (
                    <CardContent className="pt-0 pb-4">
                      <div className="border-t pt-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Steps</p>
                        <div className="flex flex-wrap gap-2">
                          {eventLog.map((evt: { node_id: string; action: string; status: string }, i: number) => {
                            const evtNode = workflow.graph_json.nodes.find(n => n.id === evt.node_id)
                            return (
                              <div key={i} className="flex items-center gap-1.5 text-xs bg-muted/50 rounded px-2 py-1">
                                {evt.status === 'success'
                                  ? <CheckCircle className="h-3 w-3 text-emerald-500" />
                                  : <XCircle className="h-3 w-3 text-red-500" />
                                }
                                <span>{(evtNode?.data?.label as string) || evt.node_id}</span>
                                <span className="text-muted-foreground">({evt.action})</span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </CardContent>
                  ) : null}
                  {/* Context preview for agent workflows */}
                  {isAgentMode && context.last_task_result ? (
                    <CardContent className="pt-0 pb-4">
                      <div className="border-t pt-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Last result</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {typeof context.last_task_result === 'object'
                            ? ((context.last_task_result as Record<string, unknown>).summary as string || JSON.stringify(context.last_task_result)).substring(0, 200)
                            : String(context.last_task_result).substring(0, 200)
                          }
                        </p>
                      </div>
                    </CardContent>
                  ) : null}
                  {/* Surface why a run stopped (cancel/fail reason). */}
                  {run.status === 'failed' && typeof context.last_error === 'string' ? (
                    <CardContent className="pt-0 pb-4">
                      <div className="border-t pt-3">
                        <p className="text-xs text-red-500 truncate" title={context.last_error as string}>
                          {context.last_error as string}
                        </p>
                      </div>
                    </CardContent>
                  ) : null}
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {/* CA-013: confirm before mass-cancelling in-flight runs. */}
      <ConfirmDeleteDialog
        open={confirmCancelAll}
        onOpenChange={setConfirmCancelAll}
        title="Cancelar todos los runs activos"
        description={`Se marcarán como fallidos los ${activeRunCount} run${activeRunCount !== 1 ? 's' : ''} en ejecución/espera de este workflow. Esta acción no se puede deshacer.`}
        confirmLabel="Cancelar runs"
        variant="warning"
        onConfirm={handleCancelAll}
        warningText="Los agent tasks ya en curso pueden ejecutarse una última vez hasta contar con la cancelación desde el backend."
      />

      {/* CA-015: assign leads to a lead workflow (previously unreachable). */}
      <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Asignar leads al workflow</DialogTitle>
            <DialogDescription>
              Cada lead seleccionado inicia un run desde el primer paso. Los leads ya enrolados no se muestran.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Buscar por nombre, empresa o cargo..."
              value={enrollSearch}
              onChange={(e) => setEnrollSearch(e.target.value)}
            />
            <ScrollArea className="h-64 rounded-md border">
              {assignableLeads.length === 0 ? (
                <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
                  {leads.length === 0 ? 'No hay leads disponibles' : 'Sin coincidencias'}
                </div>
              ) : (
                <div className="divide-y">
                  {assignableLeads.map((lead) => (
                    <label
                      key={lead.id}
                      className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-muted/50"
                    >
                      <Checkbox
                        checked={selectedLeadIds.has(lead.id)}
                        onCheckedChange={() => toggleLead(lead.id)}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {lead.first_name} {lead.last_name}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {[lead.title, lead.company].filter(Boolean).join(' · ') || '—'}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollOpen(false)} disabled={enrolling}>
              Cancelar
            </Button>
            <Button onClick={handleEnroll} disabled={enrolling || selectedLeadIds.size === 0}>
              {enrolling ? 'Asignando...' : `Asignar ${selectedLeadIds.size || ''}`.trim()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
